import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send, CheckCircle, ArrowRight } from 'lucide-react';
import {
  HOMEPAGE_HERO_EXPERIMENT,
  trackEvent,
  trackExperimentConversion,
} from '../services/analytics';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    projectDescription: '',
    budgetRange: '',
    timeline: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Use Vite environment variable for production detection
      const apiUrl = import.meta.env.PROD
        ? '/api/v1/public/project-inquiry'
        : 'http://localhost:8088/api/v1/public/project-inquiry';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          projectType: formData.projectType,
          projectDescription: formData.projectDescription,
          budgetRange: formData.budgetRange,
          timeline: formData.timeline,
          source: 'portfolio_website',
        }),
      });

      if (response.ok) {
        await response.json();
        setIsSubmitted(true);

        const conversion = {
          project_type: formData.projectType || 'not_specified',
          budget_range: formData.budgetRange || 'not_specified',
          timeline: formData.timeline || 'not_specified',
          source: 'portfolio_website',
        };
        trackEvent('project_inquiry_submitted', conversion);
        trackExperimentConversion(HOMEPAGE_HERO_EXPERIMENT, 'project_inquiry', conversion);

        // Clear form
        setFormData({
          name: '',
          email: '',
          company: '',
          projectType: '',
          projectDescription: '',
          budgetRange: '',
          timeline: '',
        });

        // Reset success message after 5 seconds
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        const errorData = await response.json();
        trackEvent('project_inquiry_failed', {
          reason: 'api_error',
          status: response.status,
          source: 'portfolio_website',
        });
        setSubmitError(errorData.message || 'Failed to submit inquiry. Please try again.');
      }
    } catch {
      trackEvent('project_inquiry_failed', {
        reason: 'network_error',
        source: 'portfolio_website',
      });
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      title: 'Email',
      detail: 'info@consltr.com',
      href: 'mailto:info@consltr.com',
    },
    {
      icon: <MapPin size={24} />,
      title: 'Based in',
      detail: 'San Francisco Bay Area',
      href: 'https://www.google.com/maps/search/San+Francisco+Bay+Area',
    },
  ];

  const projectTypes = [
    'Product Strategy',
    'New Product Build',
    'AI & Automation',
    'Platform Modernization',
    'Design & Customer Experience',
    'Other',
  ];

  const budgetRanges = [
    '$5k - $10k',
    '$10k - $25k',
    '$25k - $50k',
    '$50k - $100k',
    '$100k+',
    "Let's discuss",
  ];

  const timelineOptions = [
    '1-2 months',
    '2-3 months',
    '3-6 months',
    '6-12 months',
    '12+ months',
    'Flexible',
  ];

  return (
    <section id="contact" className="bg-canvas py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-14 grid gap-8 md:grid-cols-2 md:items-end"
        >
          <div>
            <p className="section-kicker">Start a conversation</p>
            <h2 className="magazine-title mt-4 text-5xl tracking-[-0.045em] sm:text-7xl">
              Bring us the hard part.
            </h2>
          </div>
          <p className="max-w-xl text-lg leading-8 text-muted md:justify-self-end">
            A little context is enough to start. Tell us what you are building, changing, or trying
            to understand, and we will come back with a useful next step.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="rounded-[1.75rem] border border-line/10 bg-panel p-7 sm:p-9"
          >
            <h3
              id="project-inquiry-heading"
              className="magazine-title mb-6 text-2xl font-bold text-ink"
            >
              Tell us about the work
            </h3>

            <p
              id="project-inquiry-fields"
              className="mb-6 text-sm leading-6 text-muted"
              data-agent-id="agent://consltr/contact/fields"
            >
              The inquiry form asks for your name, email, company, project type, budget range,
              timeline, and a short project description.
            </p>

            <p
              id="project-inquiry-privacy-notice"
              className="mb-6 rounded-2xl border border-line/10 bg-panel-alt p-4 text-xs leading-5 text-muted"
              data-agent-id="agent://consltr/contact/notice-at-collection"
            >
              Privacy notice at collection: Consltr uses the contact and project information you
              submit to review and respond to your inquiry, prevent abuse, and keep appropriate
              business records. Consltr does not sell it or share it for cross-context behavioral
              advertising. Read the{' '}
              <a
                href="/privacy/"
                className="font-semibold text-ink underline decoration-line/40 underline-offset-4"
              >
                Privacy Policy
              </a>
              .
            </p>

            {/* Error Message */}
            {submitError && (
              <div
                className="rounded-lg border border-red-500/50 bg-red-500/15 p-4 text-sm text-red-700 dark:text-red-300"
                role="alert"
                data-agent-id="agent://consltr/contact/delivery-error"
              >
                {submitError}
              </div>
            )}

            {/* Success Message */}
            {isSubmitted && (
              <div
                className="rounded-lg border border-green-500/50 bg-green-500/15 p-4 text-sm text-green-700 dark:text-green-300"
                role="status"
                data-agent-id="agent://consltr/contact/sent"
              >
                Thank you—your note is with us. We&apos;ll get back to you shortly.
              </div>
            )}

            <form
              id="project-inquiry"
              name="project-inquiry"
              onSubmit={handleSubmit}
              className="space-y-6"
              aria-labelledby="project-inquiry-heading"
              aria-describedby="project-inquiry-fields project-inquiry-privacy-notice project-inquiry-alternatives"
              data-agent-id="agent://consltr/contact/form"
              data-semantic-intent="submit"
              data-api-endpoint="/api/v1/public/project-inquiry"
              data-api-method="POST"
              data-api-content-type="application/json"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="magazine-subtitle mb-2 block text-sm text-muted">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-line/10 bg-field px-4 py-3 text-ink placeholder-faint transition-colors focus:border-accent focus:outline-none"
                    placeholder="Your name"
                    data-agent-id="agent://consltr/contact/name"
                    data-semantic-intent="input"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="magazine-subtitle mb-2 block text-sm text-muted"
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-line/10 bg-field px-4 py-3 text-ink placeholder-faint transition-colors focus:border-accent focus:outline-none"
                    placeholder="your@email.com"
                    data-agent-id="agent://consltr/contact/email"
                    data-semantic-intent="input"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="magazine-subtitle mb-2 block text-sm text-muted"
                >
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  autoComplete="organization"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-line/10 bg-field px-4 py-3 text-ink placeholder-faint transition-colors focus:border-accent focus:outline-none"
                  placeholder="Your company name"
                  data-agent-id="agent://consltr/contact/company"
                  data-semantic-intent="input"
                />
              </div>

              <div>
                <label
                  htmlFor="projectType"
                  className="magazine-subtitle mb-2 block text-sm text-muted"
                >
                  Project Type
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-line/10 bg-field px-4 py-3 text-ink transition-colors focus:border-accent focus:outline-none"
                  data-agent-id="agent://consltr/contact/project-type"
                  data-semantic-intent="select"
                >
                  <option value="">Select project type</option>
                  {projectTypes.map((type, index) => (
                    <option key={index} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="budgetRange"
                    className="magazine-subtitle mb-2 block text-sm text-muted"
                  >
                    Budget Range
                  </label>
                  <select
                    id="budgetRange"
                    name="budgetRange"
                    value={formData.budgetRange}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-line/10 bg-field px-4 py-3 text-ink transition-colors focus:border-accent focus:outline-none"
                    data-agent-id="agent://consltr/contact/budget"
                    data-semantic-intent="select"
                  >
                    <option value="">Select budget range</option>
                    {budgetRanges.map((range, index) => (
                      <option key={index} value={range}>
                        {range}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="timeline"
                    className="magazine-subtitle mb-2 block text-sm text-muted"
                  >
                    Timeline
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-line/10 bg-field px-4 py-3 text-ink transition-colors focus:border-accent focus:outline-none"
                    data-agent-id="agent://consltr/contact/timeline"
                    data-semantic-intent="select"
                  >
                    <option value="">Select timeline</option>
                    {timelineOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="projectDescription"
                  className="magazine-subtitle mb-2 block text-sm text-muted"
                >
                  Message *
                </label>
                <textarea
                  id="projectDescription"
                  name="projectDescription"
                  required
                  rows={6}
                  value={formData.projectDescription}
                  onChange={handleChange}
                  className="w-full resize-none rounded-xl border border-line/10 bg-field px-4 py-3 text-ink placeholder-faint transition-colors focus:border-accent focus:outline-none"
                  placeholder="Tell us about your project..."
                  data-agent-id="agent://consltr/contact/message"
                  data-semantic-intent="input"
                />
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                disabled={isSubmitting || isSubmitted}
                className="magazine-subtitle flex w-full items-center justify-center space-x-2 rounded-xl bg-highlight px-8 py-4 text-sm font-semibold text-black transition-colors hover:bg-ink hover:text-canvas disabled:opacity-50"
                data-agent-id={
                  isSubmitting
                    ? 'agent://consltr/contact/submitting'
                    : 'agent://consltr/contact/submit'
                }
                data-semantic-intent="submit"
                data-track="project_inquiry_submit"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-current"></div>
                    <span>Submitting...</span>
                  </>
                ) : isSubmitted ? (
                  <>
                    <CheckCircle size={20} />
                    <span>Message Sent!</span>
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    <span>Send Message</span>
                  </>
                )}
              </motion.button>
            </form>

            <aside
              id="project-inquiry-alternatives"
              className="mt-6 space-y-2 border-t border-line/10 pt-6 text-sm leading-6 text-muted"
              aria-label="Alternative inquiry methods"
            >
              <p>
                Prefer email?{' '}
                <a
                  href="mailto:info@consltr.com"
                  className="font-semibold text-ink underline decoration-line/40 underline-offset-4 transition-colors hover:text-accent"
                  data-agent-id="agent://consltr/contact/email-fallback"
                  data-semantic-intent="navigate"
                >
                  info@consltr.com
                </a>
                .
              </p>
              <p data-agent-id="agent://consltr/contact/api">
                For automated agents: submit truthful inquiries as JSON to{' '}
                <code className="font-mono text-xs text-ink">
                  POST /api/v1/public/project-inquiry
                </code>
                . Required fields are <code className="font-mono text-xs text-ink">name</code>,{' '}
                <code className="font-mono text-xs text-ink">email</code>, and{' '}
                <code className="font-mono text-xs text-ink">projectDescription</code>. No
                authentication is required.
              </p>
            </aside>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="magazine-title mb-6 text-3xl font-bold text-ink">
              A direct line to the people doing the work
            </h3>
            <p className="magazine-body mb-8 leading-relaxed text-muted">
              No sales maze and no speculative team. Your first conversation is with someone who can
              think through the product and the technology with you.
            </p>

            <div className="space-y-6 mb-12">
              {contactInfo.map((info, index) => (
                <motion.a
                  key={index}
                  href={info.href}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ x: 8 }}
                  className="group flex items-center space-x-4 rounded-xl border border-line/10 bg-ink/[0.03] p-4 transition-all duration-300 hover:border-accent/40"
                >
                  <div className="text-accent transition-colors">{info.icon}</div>
                  <div>
                    <div className="magazine-subtitle mb-1 text-sm text-muted">{info.title}</div>
                    <div className="text-ink transition-colors group-hover:text-accent">
                      {info.detail}
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>

            {/* Engagement fit */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="rounded-[1.5rem] border border-accent/25 bg-highlight/10 p-7"
            >
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-accent">
                A good first step
              </p>
              <h4 className="magazine-title mt-4 text-2xl font-semibold text-ink">
                Start with a working session
              </h4>
              <p className="mt-3 text-sm leading-6 text-muted">
                In one focused conversation, we can map the problem, surface the riskiest
                assumptions, and decide whether there is a useful engagement to shape together.
              </p>
              <a
                href="mailto:info@consltr.com"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent"
              >
                Email Consltr directly
                <ArrowRight size={16} aria-hidden="true" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
